import { supabase } from "@/lib/supabase-client";
import { nextSequentialNumber } from "@/lib/domain/numbering";
import { InvoiceType } from "@/types/db/invoices";
import { FileMetadata } from "@/types/files";

export const invoiceService = {
  async getInvoices(includeArchived = false): Promise<InvoiceType[]> {
    const { data, error } = await supabase.rpc("get_invoices", { p_include_archived: includeArchived });
    if (error) throw Error(error.message);
    return (data as any[]) || [];
  },

  async getInvoiceById(id: string): Promise<InvoiceType | null> {
    const { data, error } = await supabase
      .from("invoices")
      .select(
        `id,
        project_id,
        project: projects(name, id, client_id, meta_data, client: clients(id, name, meta_data)),
        created_at,
        updated_at,
        created_by,
        updated_by,
        status,
        meta_data`,
      )
      .eq("id", id)
      .single();
    if (error) {
      throw Error(error.message);
    }

    // Parse meta_data if it's a string (JSONB being returned as string)
    if (data && typeof data.meta_data === "string") {
      data.meta_data = JSON.parse(data.meta_data);
    }

    return data as unknown as InvoiceType;
  },

  async addInvoice(invoiceData: {
    tenant_id: string;
    client_id: string;
    project_id: string;
    status: string;
    meta_data?: any;
  }): Promise<InvoiceType | null> {
    const { data, error } = await supabase.from("invoices").insert([invoiceData]).select().single();
    if (error) {
      console.error("Error adding Invoice:", error.message);
      throw new Error(error.message);
    }
    return data as InvoiceType;
  },

  async getInvoicesByProjectId(project_id: string): Promise<InvoiceType[]> {
    const { data, error } = await supabase
      .from("invoices")
      .select(
        ` id, project_id,
        project: projects(name),
        created_at,
        status,
        meta_data`,
      )
      .eq("project_id", project_id)
      .order("created_at", { ascending: false });

    if (error) {
      throw Error(`Error fetching invoices by project ${project_id}\r\n${error.message}`);
    }

    return data as unknown as InvoiceType[];
  },

  async getInvoicesByClientId(client_id: string): Promise<InvoiceType[]> {
    const { data, error } = await supabase
      .from("invoices")
      .select(
        `id, project_id, client_id,
        project: projects(id, name),
        created_at,
        status,
        meta_data`,
      )
      .eq("client_id", client_id)
      .order("created_at", { ascending: false });

    if (error) {
      throw Error(`Error fetching invoices by client ${client_id}\r\n${error.message}`);
    }

    return data as unknown as InvoiceType[];
  },

  async updateInvoice(id: string, patch: Partial<InvoiceType>): Promise<InvoiceType> {
    const { data, error } = await supabase.from("invoices").update(patch).eq("id", id).select().single();
    if (error) {
      throw new Error(error.message);
    }
    return data as InvoiceType;
  },

  async saveRowPatches(patches: Partial<InvoiceType> & { id: string }[]): Promise<void> {
    await Promise.all(
      patches.map(async (patch) => {
        const { error } = await supabase.from("invoices").update(patch).eq("id", patch.id);
        if (error) throw new Error(error.message);
      }),
    );
  },

  async deleteInvoicesByIds(ids: string[]): Promise<void> {
    const { error } = await supabase.from("invoices").delete().in("id", ids);
    if (error) {
      throw Error(error.message);
    }
  },

  // File management methods
  async uploadFile(invoiceId: string, file: File, uploadedBy: string): Promise<FileMetadata> {
    // Get tenant_id from invoice record
    const { data: tenantData, error: tenantError } = await supabase
      .from("invoices")
      .select("tenant_id")
      .eq("id", invoiceId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get invoice tenant: ${tenantError?.message || "Invoice not found"}`);
    }

    const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const filePath = `${tenantData.tenant_id}/${invoiceId}/${fileId}-${file.name}`;

    // Upload file to storage
    const { error: uploadError } = await supabase.storage.from("invoices").upload(filePath, file);

    if (uploadError) {
      throw new Error(`Upload failed: ${uploadError.message}`);
    }

    // Create file metadata (store path, not URL)
    const fileMetadata: FileMetadata = {
      id: fileId,
      name: file.name,
      size: file.size,
      type: file.type,
      url: filePath, // Store path for signed URL generation
      bucket: "invoices", // Store bucket name
      uploadedAt: new Date().toISOString(),
      uploadedBy: uploadedBy,
    };

    // No database metadata tracking needed - files are listed directly from storage
    return fileMetadata;
  },

  async getFiles(invoiceId: string): Promise<FileMetadata[]> {
    const { data: tenantData, error: tenantError } = await supabase
      .from("invoices")
      .select("tenant_id")
      .eq("id", invoiceId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get invoice tenant: ${tenantError?.message || "Invoice not found"}`);
    }

    const storagePath = `${tenantData.tenant_id}/${invoiceId}`;
    const { data: files, error: listError } = await supabase.storage.from("invoices").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list invoice files: ${listError.message}`);
    }

    if (!files || files.length === 0) {
      return [];
    }

    const fileMetadata: FileMetadata[] = files.map((file) => {
      const dashIndex = file.name.indexOf("-");
      const fileId = dashIndex > 0 ? file.name.substring(0, dashIndex) : file.name;
      const fileName = dashIndex > 0 ? file.name.substring(dashIndex + 1) : file.name;

      return {
        id: fileId,
        name: fileName,
        size: file.metadata?.size || 0,
        type: file.metadata?.mimetype || "application/octet-stream",
        url: `${storagePath}/${file.name}`, // Store path for signed URL generation
        bucket: "invoices", // Store bucket name
        uploadedAt: file.created_at || new Date().toISOString(),
        uploadedBy: "unknown",
      };
    });

    return fileMetadata;
  },

  async deleteFile(invoiceId: string, fileId: string): Promise<boolean> {
    const { data: tenantData, error: tenantError } = await supabase
      .from("invoices")
      .select("tenant_id")
      .eq("id", invoiceId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get invoice tenant: ${tenantError?.message || "Invoice not found"}`);
    }

    const storagePath = `${tenantData.tenant_id}/${invoiceId}`;
    const { data: files, error: listError } = await supabase.storage.from("invoices").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list invoice files: ${listError.message}`);
    }

    const fileToDelete = files?.find((file) => file.name.startsWith(`${fileId}-`));

    if (!fileToDelete) {
      throw new Error("File not found in storage");
    }

    const filePath = `${storagePath}/${fileToDelete.name}`;
    const { error: storageError } = await supabase.storage.from("invoices").remove([filePath]);

    if (storageError) {
      throw new Error(`Failed to delete from storage: ${storageError.message}`);
    }

    return true;
  },

  async renameFile(invoiceId: string, fileId: string, newName: string): Promise<FileMetadata> {
    const { data: tenantData, error: tenantError } = await supabase
      .from("invoices")
      .select("tenant_id")
      .eq("id", invoiceId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get invoice tenant: ${tenantError?.message || "Invoice not found"}`);
    }

    const storagePath = `${tenantData.tenant_id}/${invoiceId}`;
    const { data: files, error: listError } = await supabase.storage.from("invoices").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list invoice files: ${listError.message}`);
    }

    const fileToRename = files?.find((file) => file.name.startsWith(`${fileId}-`));

    if (!fileToRename) {
      throw new Error("File not found in storage");
    }

    const oldPath = `${storagePath}/${fileToRename.name}`;
    const newPath = `${storagePath}/${fileId}-${newName}`;

    const { error: moveError } = await supabase.storage.from("invoices").move(oldPath, newPath);

    if (moveError) {
      throw new Error(`Failed to rename file: ${moveError.message}`);
    }

    return {
      id: fileId,
      name: newName,
      size: fileToRename.metadata?.size || 0,
      type: fileToRename.metadata?.mimetype || "application/octet-stream",
      url: newPath,
      bucket: "invoices",
      uploadedAt: fileToRename.created_at || new Date().toISOString(),
      uploadedBy: "unknown",
    };
  },

  async getNextInvoiceNumber(): Promise<string> {
    // Fetch all invoice numbers to find the highest and ensure uniqueness
    const { data, error } = await supabase.from("invoices").select("meta_data->invoice_number");

    if (error) {
      console.error("Error fetching invoices for number generation:", error.message);
      return "1000";
    }

    return nextSequentialNumber((data || []).map((invoice) => (invoice as any)?.invoice_number));
  },

  async getOverdueInvoicesCount(): Promise<number> {
    const { data, error } = await supabase.from("invoices").select("*").eq("status", "OVERDUE");

    if (error) {
      console.error("Error fetching pending invoices count:", error.message);
      return 0;
    }

    return data.length || 0;
  },
};
